'use client';

import { PanelLeft } from 'lucide-react';
import * as React from 'react';

import { useWindowSize } from '../../hooks/use-window-size';
import { cn } from '../../libs/utils';
import { IconButton, IconButtonProps } from '../button';

import { Sheet, SheetContent } from './sheet';

type SidebarContext = {
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
  windowWidth: number;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
  }
>(({ defaultOpen = false, onOpenChange: setOpenProp, className, style, children, ...props }, ref) => {
  const [openMobile, setOpenMobile] = React.useState(defaultOpen);

  const toggleSidebar = React.useCallback(() => {
    return setOpenMobile((open) => !open);
  }, [setOpenMobile]);

  const { windowWidth } = useWindowSize();

  const contextValue = React.useMemo<SidebarContext>(
    () => ({
      openMobile,
      windowWidth,
      setOpenMobile,
      toggleSidebar
    }),
    [openMobile, windowWidth, setOpenMobile, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        style={
          {
            '--sidebar-width': windowWidth > 428 ? '428px' : `${windowWidth}px`,
            '--sidebar-width-icon': '0px',
            ...style
          } as React.CSSProperties
        }
        className={cn('group/sidebar-wrapper has-[[data-variant=inset]]:bg-sidebar min-h-svh w-full', className)}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
});
SidebarProvider.displayName = 'SidebarProvider';

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    side?: 'left' | 'right';
    variant?: 'sidebar' | 'floating' | 'inset';
    collapsible?: 'offcanvas' | 'icon' | 'none';
  }
>(({ side = 'left', variant = 'sidebar', collapsible = 'offcanvas', className, children, ...props }, ref) => {
  const { openMobile, setOpenMobile, windowWidth } = useSidebar();

  return (
    <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
      <SheetContent
        data-sidebar="sidebar"
        data-mobile="true"
        className="z-side-bar w-[--sidebar-width] p-0 [&>button]:hidden"
        style={
          {
            '--sidebar-width': windowWidth > 428 ? '428px' : `${windowWidth}px`
          } as React.CSSProperties
        }
        side={side}
      >
        <div className="flex h-full w-full flex-col">{children}</div>
      </SheetContent>
    </Sheet>
  );
});
Sidebar.displayName = 'Sidebar';

const SidebarTrigger = React.forwardRef<HTMLButtonElement, Omit<IconButtonProps, 'iconType'>>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <IconButton
      ref={ref}
      iconType="menu"
      data-testid="sidebarTrigger"
      data-sidebar="trigger"
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </IconButton>
  );
});
SidebarTrigger.displayName = 'SidebarTrigger';

const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => {
  return <div ref={ref} data-sidebar="content" className={cn('min-h-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden', className)} {...props} />;
});
SidebarContent.displayName = 'SidebarContent';

export { Sidebar, SidebarContent, SidebarProvider, SidebarTrigger, useSidebar };
